import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import {
  GuiaBreadcrumb,
  ConversionBlock,
  PriceTag,
  GuiaFAQ,
  GuiaRelacionadas,
  SchemaArticle,
  SchemaFAQPage,
} from '../components/GuiaComponents';
import ReassuranceSection from '@/components/feature/ReassuranceSection';

const FAQS = [
  {
    question: '¿Por qué se descuelga un toldo?',
    answer:
      'Las causas más habituales son: tensores del brazo desajustados, costuras de la lona que han cedido, un brazo desnivelado respecto al otro, o la propia lona que se ha estirado con los años de uso. En Málaga, el calor extremo dilata los tejidos y acelera la pérdida de tensión, mientras que el viento de levante somete las costuras a un estrés constante.',
  },
  {
    question: '¿Se puede coser una lona de toldo en casa?',
    answer:
      'Para un pequeño descosido de pocos centímetros en el borde, puedes usar hilo de poliéster resistente a UV y una aguja gruesa, cosiendo a mano con puntadas firmes. Pero si el descosido es largo o está en la zona central, necesitas una máquina de coser industrial con hilo técnico. Las reparaciones caseras en zonas de tensión suelen fallar al poco tiempo.',
  },
  {
    question: '¿Cuánto cuesta arreglar un toldo descolgado en Málaga?',
    answer:
      'Un ajuste de tensores y nivelación de brazos cuesta entre 50 y 80 €. Si hay que reparar costuras o reforzar los bordes de la lona con cinta termoadhesiva profesional, el precio ronda los 80-120 €. En casos graves donde la lona está muy estirada, puede ser más rentable cambiarla directamente.',
  },
  {
    question: '¿El descolgamiento puede romper el toldo?',
    answer:
      'Sí, y más rápido de lo que parece. Un toldo descolgado no recoge uniformemente: un lado se enrolla más que el otro, forzando el mecanismo y desgastando los engranajes de forma asimétrica. Además, la lona suelta flamea con el viento y golpea contra los brazos, lo que puede provocar desgarros y daños en la estructura.',
  },
];

export default function GuiaToldoDescolgado() {
  return (
    <>
      <title>Toldo descolgado o descosido: cómo arreglarlo | Toldos Málaga</title>
      <meta
        name="description"
        content="¿Tu toldo está descolgado, descosido o cuelga de un lado? Guía de reparación con causas, soluciones y precios en Málaga. Reparación profesional rápida. Presupuesto gratis."
      />
      <meta
        name="keywords"
        content="toldo descolgado, reparar toldo descosido, toldo roto, toldo cuelga un lado, arreglar toldo descolgado malaga"
      />
      <link rel="canonical" href="https://toldos-malaga.es/guias/toldo-descolgado" />
      <SchemaArticle
        headline="Toldo descolgado o descosido: causas y reparación"
        description="Guía práctica para diagnosticar y solucionar un toldo descolgado o con costuras rotas. Causas más comunes en el clima de Málaga, precios de reparación y prevención."
        datePublished="2025-11-23"
        dateModified="2026-06-16"
        imageUrl="/images/005-guia-descolgado-og.webp"
        canonicalUrl="https://toldos-malaga.es/guias/toldo-descolgado"
      />
      <SchemaFAQPage faqs={FAQS} />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: 'Reparación de toldo descolgado o descosido en Málaga',
              description: 'Ajuste de tensores, nivelación de brazos y reparación de costuras en toldos descolgados. Servicio profesional en Málaga con presupuesto gratis.',
            },
            priceSpecification: {
              '@type': 'PriceSpecification',
              price: 50,
              priceCurrency: 'EUR',
            },
          }),
        }}
      />
      <Navbar />
      <main>
        <GuiaBreadcrumb currentTitle="Toldo descolgado o descosido: cómo arreglarlo" />

        <section className="w-full max-w-[1280px] mx-auto px-4 md:px-6 pt-6 pb-10">
          <div className="relative rounded-2xl overflow-hidden h-[280px] md:h-[380px]">
            <img
              src="/images/004-guia-descolgado-hero.webp"
              alt="Toldo descolgado en una terraza de Málaga"
              title="Toldo descolgado en Málaga - Reparación de costuras y tensores"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/15 to-transparent"></div>
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
              <span className="inline-block px-3 py-1 rounded-full bg-accent-500/90 text-foreground-900 text-xs font-semibold mb-3">
                Guía de reparación
              </span>
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-white">
                Toldo descolgado o descosido: cómo arreglarlo
              </h1>
            </div>
          </div>
        </section>

        <ReassuranceSection />

        <article className="w-full max-w-4xl mx-auto px-4 md:px-6 pb-14">
          <div className="prose-custom space-y-6 text-foreground-700 leading-relaxed">

            <p className="text-lg text-foreground-600">
              Extiendes el toldo y algo no cuadra: un lado cuelga más que el otro, la lona hace bolsas, o ves un hilo suelto que cada día es más largo. Un toldo descolgado no solo es antiestético; es el principio de una avería mayor si no se ataja a tiempo. En Málaga, el calor, el viento y el salitre son el cóctel perfecto para que las lonas pierdan tensión y las costuras digan basta.
            </p>

            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10">
              ¿Descolgado o descosido? Diagnostica primero
            </h2>

            <p>
              Aunque a simple vista puedan parecer lo mismo, un toldo descolgado y uno descosido tienen causas distintas y soluciones diferentes. Aquí te ayudamos a distinguirlos:
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="p-5 rounded-xl bg-secondary-100/70 border border-secondary-300/60">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-yellow-100 flex items-center justify-center">
                    <i className="ri-arrow-up-down-line text-yellow-600"></i>
                  </div>
                  <h3 className="font-heading font-bold text-foreground-900 text-base">
                    Toldo <strong>descolgado</strong>
                  </h3>
                </div>
                <p className="text-sm text-foreground-600">
                  La lona está intacta, pero no se tensa bien. Un lado está más bajo que el otro o toda la lona hace barriga. Normalmente el problema está en los tensores o en la nivelación de los brazos, no en el tejido.
                </p>
              </div>
              <div className="p-5 rounded-xl bg-secondary-100/70 border border-secondary-300/60">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center">
                    <i className="ri-link-unlink-m text-red-500"></i>
                  </div>
                  <h3 className="font-heading font-bold text-foreground-900 text-base">
                    Toldo <strong>descosido</strong>
                  </h3>
                </div>
                <p className="text-sm text-foreground-600">
                  Las costuras se han abierto y el hilo se ve suelto. Puede ser en el borde, en la unión con el perfil delantero, o en las costuras centrales. El tejido se está separando y necesita una intervención directa sobre la lona.
                </p>
              </div>
            </div>

            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10">
              Causas del descolgamiento en Málaga
            </h2>

            <div className="space-y-4">
              <div className="flex gap-3">
                <div className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-primary-600 text-xs font-bold">1</span>
                </div>
                <div>
                  <strong className="text-foreground-900">Tensores desajustados.</strong>
                  <p className="text-sm text-foreground-600 mt-1">
                    Cada brazo del toldo lleva un tensor que regula la tensión de la lona. Con el uso, las vibraciones y los cambios de temperatura (en Málaga, diferencias de 15 °C entre el día y la noche en verano), los tensores se aflojan y la lona pierde tirantez. Es la causa más frecuente y también la más fácil de solucionar.
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-primary-600 text-xs font-bold">2</span>
                </div>
                <div>
                  <strong className="text-foreground-900">Brazo desnivelado.</strong>
                  <p className="text-sm text-foreground-600 mt-1">
                    Si uno de los brazos está ligeramente más bajo que el otro —por un tornillo de fijación flojo, un soporte de pared desplazado, o un brazo ligeramente doblado— la lona se descuelga hacia ese lado. El toldo parece torcido incluso recién extendido.
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-primary-600 text-xs font-bold">3</span>
                </div>
                <div>
                  <strong className="text-foreground-900">Dilatación del tejido por calor.</strong>
                  <p className="text-sm text-foreground-600 mt-1">
                    Con temperaturas que superan los 35 °C en agosto, los tejidos —sobre todo los de poliéster— se dilatan. Tras muchos ciclos de dilatación y contracción, la lona se estira de forma permanente y ya no recupera su tensión original. Es como una goma elástica que ha dado demasiado de sí.
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-primary-600 text-xs font-bold">4</span>
                </div>
                <div>
                  <strong className="text-foreground-900">Costuras que ceden.</strong>
                  <p className="text-sm text-foreground-600 mt-1">
                    El hilo de las costuras se degrada con los rayos UV y la humedad. En la costa malagueña, el salitre acelera este proceso. Las costuras del borde delantero son las primeras en fallar, porque soportan la mayor tensión cada vez que el toldo se extiende y se recoge.
                  </p>
                </div>
              </div>
            </div>

            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10">
              ¿Lo arreglo yo o llamo a un toldero?
            </h2>

            <p>
              Si el problema es solo un tensor flojo, puedes intentar ajustarlo tú mismo. El tensor suele ser una pieza metálica cerca del codo del brazo, con una tuerca que se gira para aumentar o disminuir la tensión. Hazlo con el toldo extendido y en pequeños incrementos, comprobando tras cada ajuste que la lona queda tirante pero sin forzar.
            </p>

            <p>
              Para un pequeño descosido en el borde —menos de 10 centímetros—, puedes coserlo a mano con hilo de poliéster resistente a UV (lo venden en mercerías) y una aguja gruesa. Usa puntadas cortas y firmes, y si puedes, refuerza la zona con cinta termoadhesiva para toldos por la parte interior.
            </p>

            <div className="p-4 rounded-xl bg-[#FEF3C7] border border-yellow-300/60">
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-yellow-400/80 flex items-center justify-center flex-shrink-0">
                  <i className="ri-error-warning-line text-foreground-900 text-base"></i>
                </div>
                <div>
                  <strong className="text-foreground-900 text-sm">Pero ojo:</strong>
                  <p className="text-sm text-foreground-700 mt-1">
                    Si el descosido es largo o está en una zona de tensión, coser a mano no aguantará. Hace falta una máquina industrial con hilo técnico. Y si el toldo está desnivelado por un brazo doblado, no lo toques: la estructura está comprometida y requiere sustitución profesional.
                  </p>
                </div>
              </div>
            </div>

            <div className="my-8">
              <ConversionBlock
                title="¿Toldo descolgado en Málaga? Lo tensamos hoy"
                subtitle="Ajustamos tensores, reparamos costuras y nivelamos brazos. Respuesta en 24 horas en Málaga capital y alrededores. Presupuesto gratis."
              />
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 my-8">
              <PriceTag price="desde 50 €" description="Ajuste de tensores" />
              <PriceTag price="desde 80 €" description="Reparación costuras" />
              <p className="text-sm text-foreground-500">
                Precios orientativos. Si la lona está muy deteriorada puede ser más rentable cambiarla.
              </p>
            </div>

            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10">
              Preguntas frecuentes
            </h2>

            <GuiaFAQ faqs={FAQS} />

            <div className="mt-12">
              <GuiaRelacionadas />
            </div>

          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}